

import { useContext, useEffect, useState } from 'react';
import { Button, Collapse, Tab, Tabs } from 'react-bootstrap';
import { useTranslation, withTranslation } from 'react-i18next';
import { AiOutlineClose } from "react-icons/ai";
import { BiChevronDown } from 'react-icons/bi';
import { CgSortAz } from "react-icons/cg";
import { FcFinePrint } from "react-icons/fc";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoaderContext, PreferenceContext, RegulationContext } from "../../../App";
import AITextGenWidget from '../../../components/AI/AITextGenWidget';
import AppFilter from '../../../components/AppFilter/AppFilter';
import { DataViewCategoryType, LS_AUTHORITY_KEY, LS_ESG_FILTER_KEY } from '../../../core/constants/common';
import { getCompanyInfoFromStorage, getDefaultSelectableYear } from '../../../core/utilities/assessmentPeriodUtils';
import CompanyService from '../../../core/service/CompanyService';
import PermissionUtils from '../../../core/utilities/PermissisonUtils';
import CompanyQuestionContainer from './CompanyQuestionContainer';
import ContentBlocks from './ContentBlocks';
import './EnterpriseStructure.scss';
import GeneralDisclosure from './GeneralDisclosure';
import QualitativeFilterStatus from './QualitativeFilterStatus';

const MaterialTopic = ({ t, embeddedView, yearFilter, onDataUpdate }) => {
  const { glbRegulation } = useContext(RegulationContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Utility functions for local storage operations
  const saveESGFilterToStorage = (filterData) => {
    try {
      localStorage.setItem(LS_ESG_FILTER_KEY, JSON.stringify(filterData));
    } catch (error) {
      console.warn('Failed to save ESG filter to local storage:', error);
    }
  };

  const getESGFilterFromStorage = () => {
    try {
      const savedFilter = localStorage.getItem(LS_ESG_FILTER_KEY);
      return savedFilter ? JSON.parse(savedFilter) : null;
    } catch (error) {
      console.warn('Failed to parse saved ESG filter:', error);
      return null;
    }
  };

  // Initialize filter with local storage or default values
  const getInitialESGFilter = () => {
    const urlPeriod = searchParams.get('period');
    
    if (urlPeriod) {
      return { 
        reportingPeriod: urlPeriod, 
        categoryType: DataViewCategoryType.ENVIRONMENTAL, 
        topicIds: [] 
      };
    } else {
      const savedFilter = getESGFilterFromStorage();
      return savedFilter || { 
        reportingPeriod: `${yearFilter ?? getDefaultSelectableYear(getCompanyInfoFromStorage())}-01-01`, 
        categoryType: DataViewCategoryType.ENVIRONMENTAL, 
        topicIds: [] 
      };
    }
  };

  const [filter, setFilter] = useState(getInitialESGFilter());
  const [questions, setQuestions] = useState({});
  const [answers, setAnswers] = useState({});
  const [topicForAITextGen, setTopicForAITextGen] = useState({ id: 0 });
  const [activeHelpText, setActiveHelpText] = useState('');
  const { setLoaderDisplay } = useContext(LoaderContext);
  const [pgnum, setPgnum] = useState(0);
  const [topics, setTopics] = useState([]);
  const [topicDisplay, setTopicDisplay] = useState({});
  const [helpActive, setHelpActive] = useState(false)
  const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const [showAIContentExtractWidget, setShowAIContentExtractWidget] = useState(false);
  const [extractedContent, setExtractedContent] = useState('');
  const [filterDisplay, setFilterDisplay] = useState(false);
  const [activeTab, setActiveTab] = useState('ESG');
  const [codeMapping, setCodeMapping] = useState({});
  const { k, i18n } = useTranslation();
  const { preference } = useContext(PreferenceContext);

  useEffect(() => {
    if (!glbRegulation.id) return;

    setTopicDisplay({});
    setQuestions({});
    setAnswers({});
  }, [glbRegulation.id, filter.categoryType, filter.reportingPeriod]);


  const getMetricQuestions = (standard, period, topicId) => {
    setLoaderDisplay(true);
    CompanyService.getMetricQuestions(standard, period, filter.categoryType, topicId)
      .then(res => {
        setLoaderDisplay(false);
        const q = JSON.parse(JSON.stringify(questions));
        if (res.data?.questions) {
          initializeAnswers(topicId, res.data?.questions);
            q[topicId] = res.data?.questions;
            setQuestions(q);
        } else {
          q[topicId] = [];
          setQuestions(q);
          const a = JSON.parse(JSON.stringify(answers));
          a[topicId] = {};
          setAnswers(a);
        }
        const m = JSON.parse(JSON.stringify(codeMapping));
        if (res.data?.mapping) {
          m[topicId] = res.data?.mapping;
        } else {
          m[topicId] = {};
        }
        setCodeMapping(m);
      })
      .catch((error) => {
        setLoaderDisplay(false);
      });
  };

  const initializeAnswers = (topicId, questions) => {
    const initialAnswers = JSON.parse(JSON.stringify(answers));
    initialAnswers[topicId] = {};
    questions?.forEach(question => {
      if (question.children?.length > 0) {
        question?.children?.forEach(childQuestion => {
          const ans = [];
          if (childQuestion.answers?.length > 0) {
            childQuestion.answers
            ?.sort((a, b) => +(a.cell) - +(b.cell))
            ?.forEach(answer => {
              ans.push({
                id: answer.id,
                answer: answer.answer,
                cell: answer.cell ?? '1',
                remark: answer.remark ?? '',
                attachments: answer.attachments ?? []
              });
            });
          } else {
            ans.push({
              id: 0,
              answer: '',
              cell: '1',
              edited: false
            });
          }
          initialAnswers[topicId][childQuestion.id] = ans;
        });
      } else {
        const ans = [];
        if (question.answers?.length > 0) {
          question.answers
          ?.sort((a, b) => +(a.cell) - +(b.cell))
          ?.forEach(answer => {
            ans.push({
              id: answer.id,
              answer: answer.answer,
              cell: answer.cell ?? '1',
              remark: answer.remark ?? '',
              attachments: answer.attachments ?? []
            });
          });
        } else {
          ans.push({
            answer: '',
            cell: '1',
            edited: false
          });
        }
        initialAnswers[topicId][question.id] = ans;
      }
    });
    setAnswers(initialAnswers);
  };

  const setReportingPeriod = (period) => {
    const [year, month] = period.split('-');
    const f = { ...filter, reportingPeriod: `${year}-01-01` };
    setFilter(f);
    // if (PermissionUtils.isCompanyAdmin(lsData.roles) || PermissionUtils.isConsultant(lsData.roles)) {
    //   refreshAllQuestionsAndAnswersInDisplay(f.reportingPeriod);
    // } else {
    //   setTopicDisplay({});
    // }
    if(PermissionUtils.isSuperAdmin(lsData.roles)){
      setTopicDisplay({});
    }else{
      refreshAllQuestionsAndAnswersInDisplay(f.reportingPeriod);
    }
  };

  const renderQuestionHelpTextContainer = () => {
    if (!activeHelpText) return <></>;
    
    let lines = activeHelpText.helpText.split('\n');
    const paras = [];

    if (activeHelpText.localizedContents && activeHelpText.localizedContents.length > 0) {
      const translatedDetailsArr = activeHelpText.localizedContents.find(item => item.fieldName === 'help_text' && item.languageCode === preference.language);
      
      if (translatedDetailsArr) {
        lines = translatedDetailsArr.translatedValue.split('\n');
      }
    }

    lines.forEach((line) => {
      //checks for roman numeral followed by dot present,if present put padding there,
      if (/^\s*([ivxlmIVXLCDM]+)\./.test(line)) {
        paras.push(<p className='mt-0 mb-0 px-4'>{line}</p>);
      } else if (/^\s*[a-zA-Z]+\./.test(line)) {
        paras.push(<p className='mt-0 mb-0 px-2'>{line}</p>);
      } else {
        paras.push(<p className='mt-0 mb-0'>{line}</p>);
      }
    });
    return (
      <div className='col-md-4 p-2'>
        {

          helpActive &&
          <div className='card Help_text_Card' style={{ top: activeHelpText?.top }}>
            <div className='card-body'>
              <Button variant="Help_text_Close" onClick={() => setActiveHelpText('')}><AiOutlineClose /></Button>
              <h6 className='fw-bold'>{t('HelpText.label')}</h6>
              <p>{lines}</p>
                {
                  activeHelpText.sampleAnswer && (
                    <div>
                      <h6 className='fw-bold'>{t('SampleAnswer.label')}</h6>
                      {activeHelpText.localizedContents && activeHelpText.localizedContents.length > 0 ? (
                        activeHelpText.localizedContents
                          .filter(item => item.fieldName === 'sample_answer' && item.languageCode === preference.language).length > 0
                          ? activeHelpText.localizedContents.filter(item => item.fieldName === 'sample_answer' && item.languageCode === preference.language)
                            .map(item => (
                              <p key={item.id}>{item.translatedValue}</p>
                            ))
                          : <p>{activeHelpText.sampleAnswer}</p>
                      ) : (
                        <p>{activeHelpText.sampleAnswer}</p>
                      )}
                    </div>
                  )
                }
            </div>

          </div>
        }

      </div>
    );
  };

  const findAndUpdateQuestionStatus = (topicId, qId, status) => {
    const ques = JSON.parse(JSON.stringify(questions));
    for (let i = 0; i < ques[topicId]?.length; i++) {
      const q = ques[topicId][i];
      if (q.id === qId) {
        q.disabled = status;
        break;
      }
      let found = false;
      for (let j = 0; j < q.children.length; j++) {
        const child = q.children[j];
        if (child.id === qId) {
          child.disabled = status;
          found = true;
          break;
        }
      }
      if (found) break;
    }
    setQuestions(ques);
  };

  const getSelectedTopics = () => {
    const data = [];
    topics?.forEach(topic => {
      if (filter.topicIds.indexOf(topic.id) < 0) return;

      data.push(topic);
    });
    return data;
  };

  const getTopicsToDisplay = () => {
    if (filter.topicIds.length === 0) return topics;

    return getSelectedTopics();
  };

  const refreshAllQuestionsAndAnswersInDisplay = (reportingPeriod, topicId = -1) => {
    if (topicId > 0) {
      getMetricQuestions(glbRegulation.id, reportingPeriod, topicId);
      return;
    }
    Object.keys(topicDisplay)?.forEach(tId => {
      if (topicDisplay[tId] !== true) return;

      getMetricQuestions(glbRegulation.id, reportingPeriod, tId);
    });
  };

  const hideAIContentExtract = () => {
    setShowAIContentExtractWidget(false);
  }

  const setContentExtract = (content) => {
    setExtractedContent(content);
  }

  const handleTabChange = (key) => {
    setActiveTab(key);
    setFilterDisplay(false);
  };

  const renderFilter = () => {
    return (
      <div className={filterDisplay && activeTab === 'ESG' ? '' : 'd-none'}>
        <AppFilter
          type={'NR_ESG'}
          existingFilter={{ period: filter.reportingPeriod }}
          esgFilter={filter}
          onApplyFilter={(f) => {
            setTopics(f.topicsFromFilter);
            setReportingPeriod(f.period);
            const newFilter = { reportingPeriod: f.period, categoryType: f.categoryType, topicIds: f.selectedTopicIds };
            setFilter(newFilter);
            
            // Save filter to local storage for persistence
            saveESGFilterToStorage(newFilter);
          }}
          onFilterHide={(value) => setFilterDisplay(value)} />
      </div>
    )
  };

  const handleHelpTextCallback = (q) => {
    setTopicForAITextGen({ id: 0 });
    setHelpActive(true);
    setActiveHelpText(q);
  };

  return (
    <div className='MaterialTopic'>
      <div className="List_header">
        <div><h5 className="MH">{t("OneTime.label")}</h5></div>
      </div>
      <div className='MaterialTopic_tabbox'>
        {/* <div className="d-flex align-items-center justify-content-between mb-2"> */}
          <Tabs
            defaultActiveKey="ESG"
            id="uncontrolled-tab-example"
            className="mb-0"
            onSelect={handleTabChange}>
            <Tab eventKey="ESG" title={t("ESGNarratives.label")}></Tab>
            <Tab eventKey="SUPPLEMENTARY" title={t("SupplementaryInformation.label")}></Tab>
          </Tabs>
          {activeTab === 'ESG' && (
            <div className="Onetime_filter">
            <QualitativeFilterStatus 
              filter={filter}
              topics={topics}
              onFilterClick={() => setFilterDisplay(!filterDisplay)}
              showViewToggle={!embeddedView}
              onTableViewNavigate={
                !embeddedView
                  ? () =>
                      navigate(
                        `/company_admin/qualitative_table_search?period=${encodeURIComponent(filter.reportingPeriod)}`
                      )
                  : undefined
              }
            />
            </div>
          )}
        {/* </div> */}
        <div className="tab-content">
          {activeTab === 'ESG' && (
            <div className="tab-pane active">
              <div className='row m-0'>
              <div className={`${topicForAITextGen.id > 0 ? 'col-md-8' : 'col-md-11'} p-0`}>
                {renderFilter()}
                <div className='contentBlocks_head_in'>{t("ESRInfo.label")}</div>
                <div className='pt-3'>
                  {
                    getTopicsToDisplay()?.map((topic, topicIndex) => {
                      return (
                        <div key={`topicGroup-${topicIndex}`} className="card mb-2">
                          <div className="Qualitative_qusBox">
                            <span onClick={(e) => {
                              const d = JSON.parse(JSON.stringify(topicDisplay));
                              d[topic.id] = d[topic.id] !== null ? !d[topic.id] : true
                              setTopicDisplay(d);
                              if (d[topic.id] === false) return;

                              getMetricQuestions(glbRegulation.id, filter.reportingPeriod, topic.id);
                            }}>
                              <BiChevronDown />
                            </span>
                            {
                              topic.localizedContents && topic.localizedContents.length > 0 ? (
                                topic.localizedContents.filter(item => item.fieldName === 'name' && item.languageCode === preference.language).length > 0
                                  ? topic.localizedContents.filter(item => item.fieldName === 'name' && item.languageCode === preference.language)
                                    .map(item => (
                                      <h6 className="mb-0" key={item.id}>{item.translatedValue}</h6>
                                    ))
                                  : <h6 className="mb-0">{topic.name}</h6>
                              ) : (
                                <h6 className="mb-0">{topic.name}</h6>
                              )
                            }
                          </div>
                          <Collapse in={topicDisplay[topic.id] === true}
                            onExited={() => setHelpActive(false)}>
                            <div className="card-body p-1 border-top">
                              {
                                questions[topic.id]?.length > 0 &&
                                <CompanyQuestionContainer
                                  pgnum={pgnum}
                                  ansData={answers[topic.id]}
                                  questions={questions[topic.id]}
                                  filter={filter}
                                  type='MATERIAL_TOPIC'
                                  helpTextId={activeHelpText?.id ?? ''}
                                  onRefineWithAIClick={(q) => {
                                    setTopicForAITextGen({
                                      id: q.id,
                                      materialtopic: q.materialtopic,
                                      question: q.question,
                                      guideline: q.guideline,
                                      answer: q.answer,
                                      questionCode: q.questionCode,
                                      mapping: codeMapping[topic.id][q.questionCode]
                                    });
                                    setHelpActive(false);
                                    setShowAIContentExtractWidget(false);
                                  }}
                                  onDisplayHelpText={
                                    (q) => {
                                      if (activeHelpText?.id === q.id) {
                                        setActiveHelpText('');
                                        setTopicForAITextGen({ id: 0 });
                                        setHelpActive(true);
                                      } else {
                                        setActiveHelpText(q);
                                        setTopicForAITextGen({ id: 0 });
                                        setHelpActive(true);
                                      }
                                    }
                                  }
                                  refreshData={() => {
                                    refreshAllQuestionsAndAnswersInDisplay(filter.reportingPeriod, topic.id);
                                    if (!embeddedView) return;

                                    onDataUpdate();
                                  }}
                                  changeQuestionStatus={(qId, status) => findAndUpdateQuestionStatus(topic.id, qId, status)} />
                              }
                              {
                                questions[topic.id]?.length == 0 &&
                                <div className="Nodatafound_box">
                                  <h6><FcFinePrint className="me-2" />{t("NoDataAvailable.label")}</h6>
                                </div>
                              }
                            </div>
                          </Collapse>
                        </div>
                      )
                    })
                  }
                  <GeneralDisclosure embeddedView={false}                    
                    reportingPeriod={filter.reportingPeriod} 
                    onHelpTextCallback={handleHelpTextCallback}/>
                </div>
              </div>
              {
                topicForAITextGen?.id > 0 &&
                <div className='col-md-4 p-2'>
                  <AITextGenWidget type='MATERIAL_TOPIC'
                    materialTopic={topicForAITextGen}
                    onHide={() => {
                      setTopicForAITextGen({ id: 0 });
                      setHelpActive(true);
                    }}
                    onTransferToEditor={(text) => {
                      const ans = JSON.parse(JSON.stringify(answers));
                      ans[topicForAITextGen.id][0].answer = text;
                      setAnswers(ans);
                      setTopicForAITextGen({ id: 0 });
                    }} />
                </div>
              }
              {renderQuestionHelpTextContainer()}
              </div>
            </div>
          )}
          {activeTab === 'SUPPLEMENTARY' && (
            <div className="tab-pane active">
              <div className='row m-0'>
                <ContentBlocks
                  showAIWidgetScreen={(show) => { setShowAIContentExtractWidget(show); setTopicForAITextGen({ id: 0 }); setHelpActive(false); }}
                  closeWidget={(value) => { setShowAIContentExtractWidget(value); }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(MaterialTopic);
